import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcJpmamV1JpmamOrderQueryServiceJpmamOrderGet } from '@interxfi/api-client/generated/hooks/exc-jpmam-v1-jpmam-order-query-service-jpmam-order-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcJpmamV1JpmamOrderState } from '@interxfi/types';
import { JpmamActionTypePill } from '../../../../../../../../../../features/vendor/jpmam/jpmam-action-type-pill';
import { JpmamOrderEntityMenu } from '../../../../../../../../../../features/vendor/jpmam/order/jpmam-order-entity-menu';
import { JpmamOrderEventSummary } from '../../../../../../../../../../features/vendor/jpmam/order/jpmam-order-event';
import { getJpmamOrderListItems } from '../../../../../../../../../../features/vendor/jpmam/order/jpmam-order-list-items';
import { getJpmamOrderDetailPath } from '../../../../../../../../../../features/vendor/jpmam/order/jpmam-order-routing';
import { JpmamOrderStatusPill } from '../../../../../../../../../../features/vendor/jpmam/order/jpmam-order-status-pill';
import { JpmamOrderSummaryBar } from '../../../../../../../../../../features/vendor/jpmam/order/jpmam-order-summary-bar';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/jpm/order/_order/$orderId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `jpmam-order-${params.orderId}`,
        label: 'Order',
        to: getJpmamOrderDetailPath(params.orderId),
        suffix: <CopyButton content={params.orderId} label="Copy Order ID" tooltip={{ copy: params.orderId, copied: 'Copied Order ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('JPMAM Order'),
  component: JpmamOrderDetail,
});

function getListItems(data: ExcJpmamV1JpmamOrderState | undefined) {
  const items = getJpmamOrderListItems(data);
  const source = items.data.spec.source;

  return {
    left: [items.data.spec.quantity, items.data.spec.unit, items.data.spec.actionPill] as const satisfies ReadonlyListItem[],
    right: [
      items.orderId,
      items.data.spec.assetId,
      source.typeName,
      ...('customer' in source ? [source.customer.value.orderId, source.customer.value.accountId, source.customer.value.programId] : []),
      { ...items.data.spec.timestamp, title: 'Created At' },
      items.data.completeTimestamp,
      items.data.settlementDate,
      ...(items.data.groupingId ? [items.data.groupingId] : []),
    ] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load JPMAM order', 'view this JPMAM order');

function JpmamOrderDetail() {
  const { orderId } = Route.useParams();
  const request = { orderId } as const;
  const { data, error, isPending } = useExcJpmamV1JpmamOrderQueryServiceJpmamOrderGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.jpmamOrder), [data?.jpmamOrder]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <JpmamOrderStatusPill isPending={showPending} size="large" status={data?.jpmamOrder?.status} />
            {showPending || data?.jpmamOrder?.data?.spec?.action ? (
              <JpmamActionTypePill action={data?.jpmamOrder?.data?.spec?.action} isPending={showPending} size="large" />
            ) : null}
          </HorizontalStack>

          <JpmamOrderEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <JpmamOrderSummaryBar {...request} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <JpmamOrderEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
